import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import React, { useState } from 'react';
import DropzoneComponent from './DropZoneComponent';
import { Transition } from './Transition';
import { LoadingButton } from '@mui/lab';
import Scrollbar from './Scrollbar';
import { toast } from 'react-toastify';
import { getAttachmentAssetUrl } from '../utils/url';

export type UploadedAttachmentPreview = {
  id: string;
  key?: string;
  fileName?: string;
  fileType?: string;
  url?: string;
};

interface IProps {
  toggle: boolean;
  onClose: () => void;
  setAttachmentArray: React.Dispatch<React.SetStateAction<any>>;
  module: string;
  isSingleUpload?: boolean;
  moduleId?: string;
  setPreviewAttachments?: React.Dispatch<React.SetStateAction<any>>;
  onUploadedAttachments?: (attachments: UploadedAttachmentPreview[]) => void;
  /** Already-uploaded attachments to show when the modal opens */
  existingAttachments?: UploadedAttachmentPreview[];
}

interface File {
  path: string;
  name: string;
  preview: string;
  type: string;
  loader?: boolean;
  id?: string;
  key?: string;
  mimeType?: string;
  retry?: boolean;
}

const isBlobUrl = (url?: string) =>
  typeof url === 'string' && url.startsWith('blob:');

/** Prefer stable API/CDN URL — never reuse revoked blob: previews */
const resolveStablePreview = (att: UploadedAttachmentPreview): string => {
  const fromKey = getAttachmentAssetUrl({
    id: att.id,
    key: att.key,
    fileName: att.fileName,
    fileType: att.fileType,
  });
  if (fromKey && !isBlobUrl(fromKey)) {
    return fromKey;
  }

  if (att.url && !isBlobUrl(att.url)) {
    return att.url;
  }

  // Reconstruct storage key when missing (upload create returns keypath as key)
  if (att.id) {
    const ext =
      att.fileName?.split('.').pop()?.toLowerCase() ||
      (att.fileType?.includes('png') ? 'png' : 'jpg');
    return getAttachmentAssetUrl({
      id: att.id,
      key: att.key || `advertise/${att.id}.${ext}`,
      fileName: att.fileName,
      fileType: att.fileType,
    });
  }

  return '';
};

const toModalFiles = (
  attachments: UploadedAttachmentPreview[] = []
): File[] =>
  attachments
    .filter((att) => Boolean(att?.id))
    .map((att) => {
      const preview = resolveStablePreview(att);

      return {
        id: att.id,
        key:
          att.key ||
          (att.id
            ? `advertise/${att.id}.${
                att.fileName?.split('.').pop()?.toLowerCase() || 'jpg'
              }`
            : undefined),
        name: att.fileName || 'image',
        path: att.fileName || 'image',
        type: att.fileType || 'image/jpeg',
        mimeType: att.fileType || 'image/jpeg',
        preview,
        loader: false,
        retry: false,
      };
    })
    .filter((file) => Boolean(file.preview));

const UploadAssetsModal: React.FC<IProps> = ({
  toggle,
  onClose,
  setAttachmentArray,
  module,
  isSingleUpload,
  moduleId,
  setPreviewAttachments,
  onUploadedAttachments,
  existingAttachments = [],
}) => {
  const [files, setFiles] = useState<File[]>([]);

  // Seed with existing form images only when the modal opens
  React.useEffect(() => {
    if (toggle) {
      setFiles(toModalFiles(existingAttachments));
    } else {
      setFiles([]);
    }
  }, [toggle]); // existingAttachments read only at open time

  const isFileReady = (file: File) =>
    Boolean(file.id) && file.loader !== true && !file.retry;

  const isUploadInProgress = files.some(
    (file) => Boolean(file.loader) || (!file.id && !file.retry)
  );
  const hasFailedUploads = files.some((file) => Boolean(file.retry));
  const canConfirmUpload =
    files.length > 0 && files.every(isFileReady);

  const toPreviewAttachments = (
    uploadedFiles: Array<File & { id: string }>
  ): UploadedAttachmentPreview[] =>
    uploadedFiles.map((file) => {
      const key =
        file.key ||
        `advertise/${file.id}.${
          file.name?.split('.').pop()?.toLowerCase() || 'jpg'
        }`;
      const stableUrl = resolveStablePreview({
        id: file.id,
        key,
        fileName: file.name,
        fileType: file.mimeType || file.type,
      });

      return {
        id: file.id,
        key,
        fileName: file.name,
        fileType: file.mimeType || file.type,
        // Prefer stable URL so reopening the modal does not use revoked blob:
        url: stableUrl || (!isBlobUrl(file.preview) ? file.preview : undefined),
      };
    });

  const syncAttachmentsToForm = (
    uploadedFiles: Array<File & { id: string }>
  ) => {
    const ids = uploadedFiles.map((file) => file.id);
    // Modal holds the full current list (including previously uploaded ones)
    setAttachmentArray(ids);
    onUploadedAttachments?.(toPreviewAttachments(uploadedFiles));
  };

  const onSubmitUpload = async () => {
    // Only confirm when every selected file finished successfully
    const uploadedFiles = files.filter(
      (element): element is File & { id: string } => isFileReady(element)
    );
    const AttachmentArray: string[] = uploadedFiles.map(
      (element) => element.id
    );

    if (!isSingleUpload) {
      if (
        AttachmentArray.length === files.length &&
        AttachmentArray.length > 0
      ) {
        syncAttachmentsToForm(uploadedFiles);
        onClose();
      } else if (isUploadInProgress) {
        toast.error('Wait until assets are uploaded.');
      } else if (hasFailedUploads) {
        toast.error('Please retry failed uploads before continuing.');
      } else {
        toast.error('Please upload at least one asset.');
      }
    } else {
      // For single upload: must have exactly 1 file and it must be uploaded
      if (files?.length === 0) {
        toast.error('Please select a file to upload.');
      } else if (files?.length > 1) {
        toast.error('Only one asset is allowed.');
      } else if (isUploadInProgress || AttachmentArray?.length === 0) {
        toast.error('Wait until the asset is uploaded.');
      } else if (AttachmentArray?.length === 1) {
        syncAttachmentsToForm(uploadedFiles);
        onClose();
      } else {
        toast.error('Only one asset is allowed.');
      }
    }
  };

  return (
    <>
      <Dialog
        fullWidth
        open={toggle}
        TransitionComponent={Transition}
        keepMounted
        onClose={onClose}
        aria-describedby='alert-dialog-slide-description'
      >
        <DialogTitle>Upload Files</DialogTitle>
        <DialogContent>
          <Scrollbar>
            <DropzoneComponent
              setFiles={setFiles}
              files={files}
              module={module}
              moduleId={moduleId}
              onClose={onClose}
              setPreviewAttachments={setPreviewAttachments}
            />
          </Scrollbar>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setFiles([]);
              onClose();
            }}
            variant='outlined'
          >
            Cancel
          </Button>
          <LoadingButton
            variant='contained'
            disabled={!canConfirmUpload}
            loading={isUploadInProgress}
            onClick={() => {
              onSubmitUpload();
              if (canConfirmUpload) {
                setTimeout(() => {
                  setFiles([]);
                }, 100);
              }
            }}
          >
            Upload
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UploadAssetsModal;
